/**
 * Authorization model: Role + Capability + Resource Scope.
 *
 * Roles are predefined system roles. Each role grants a default set of capabilities,
 * each bound to a scope. Tenants may adjust capabilities for tenant roles within the
 * `customizable` bounds declared in CAPABILITIES; locked capabilities can never be
 * granted to or removed from a role by tenant configuration.
 */

export const TENANT_ROLES = [
  'tenant_owner',
  'tenant_admin',
  'account_manager',
  'specialist',
  'auditor',
] as const;

export const CLIENT_ROLES = ['client_admin', 'client_contributor', 'client_approver'] as const;

export const PLATFORM_ROLES = ['platform_operator'] as const;

export type TenantRole = (typeof TENANT_ROLES)[number];
export type ClientRole = (typeof CLIENT_ROLES)[number];
export type PlatformRole = (typeof PLATFORM_ROLES)[number];
export type Role = TenantRole | ClientRole | PlatformRole;

export const ROLE_LABELS: Record<Role, string> = {
  platform_operator: 'Platform Operator',
  tenant_owner: 'Tenant Owner',
  tenant_admin: 'Tenant Administrator',
  account_manager: 'Account Manager',
  specialist: 'Specialist / Reviewer',
  auditor: 'Read-only Auditor',
  client_admin: 'Client Administrator',
  client_contributor: 'Client Contributor',
  client_approver: 'Client Approver',
};

/**
 * Staff roles that may be a client's account manager. Account managers and administrators are the
 * normal choice; the owner is eligible only when picked explicitly (always shown with their role
 * label, never assigned as a fallback). Enforced identically by the API on create and edit.
 */
export const ACCOUNT_MANAGER_ROLES = ['account_manager', 'tenant_admin', 'tenant_owner'] as const satisfies readonly TenantRole[];

export function isTenantRole(role: string): role is TenantRole {
  return (TENANT_ROLES as readonly string[]).includes(role);
}
export function isClientRole(role: string): role is ClientRole {
  return (CLIENT_ROLES as readonly string[]).includes(role);
}

/**
 * Scope semantics:
 *  - tenant:   every resource of that type inside the caller's tenant
 *  - assigned: only engagements (and their children) where the caller is an
 *              engagement participant, the engagement owner, or the client's
 *              assigned account manager
 *  - client:   only resources inside the caller's own client boundary (client administrators: every
 *              engagement of their client; contributors/approvers: the engagements they were
 *              invited to), further narrowed to the participant's locations
 */
export type Scope = 'tenant' | 'assigned' | 'client';

export const CAPABILITIES = {
  // Tenant / identity
  'tenant.settings.manage': { label: 'Manage tenant settings & branding', audience: 'tenant', customizable: false },
  'tenant.billing.manage': { label: 'Manage subscription & billing', audience: 'tenant', customizable: false },
  'tenant.ownership.transfer': { label: 'Transfer tenant ownership', audience: 'tenant', customizable: false },
  'staff.view': { label: 'View staff', audience: 'tenant', customizable: true },
  'staff.manage': { label: 'Invite & manage staff', audience: 'tenant', customizable: true },
  'roles.manage': { label: 'Configure role capabilities', audience: 'tenant', customizable: false },
  // Clients
  'client.view': { label: 'View clients', audience: 'both', customizable: true },
  'client.create': { label: 'Create clients', audience: 'tenant', customizable: true },
  'client.edit': { label: 'Edit clients, brands, locations, contacts', audience: 'tenant', customizable: true },
  'client.archive': { label: 'Archive clients', audience: 'tenant', customizable: true },
  'client.profile.update': { label: 'Apply shared profile changes', audience: 'both', customizable: true },
  // Configuration
  'templates.view': { label: 'View template library', audience: 'tenant', customizable: true },
  'templates.manage': { label: 'Create, edit & publish templates', audience: 'tenant', customizable: true },
  'packages.manage': { label: 'Create & publish packages', audience: 'tenant', customizable: true },
  'workflow.manage': { label: 'Configure workflows & approvals', audience: 'tenant', customizable: true },
  'automation.manage': { label: 'Configure reminder automation', audience: 'tenant', customizable: true },
  // Engagements
  'engagement.view': { label: 'View engagements', audience: 'both', customizable: true },
  'engagement.create': { label: 'Create engagements', audience: 'tenant', customizable: true },
  'engagement.manage': { label: 'Manage engagements (transitions, scope, due dates)', audience: 'tenant', customizable: true },
  'engagement.migrate': { label: 'Migrate engagements to new template versions', audience: 'tenant', customizable: true },
  'engagement.handoff': { label: 'Confirm handoff & completion', audience: 'tenant', customizable: true },
  'invitation.manage': { label: 'Invite & revoke client participants', audience: 'both', customizable: true },
  'participant.manage': { label: 'Manage client-side people', audience: 'both', customizable: true },
  // Forms
  'form.view': { label: 'View forms & answers', audience: 'both', customizable: true },
  'form.answer.edit': { label: 'Edit form answers', audience: 'both', customizable: true },
  'form.submit': { label: 'Submit forms', audience: 'both', customizable: true },
  'form.review': { label: 'Review submissions', audience: 'tenant', customizable: true },
  // Evidence
  'asset.view': { label: 'View assets', audience: 'both', customizable: true },
  'asset.upload': { label: 'Upload assets', audience: 'both', customizable: true },
  'asset.review': { label: 'Review assets', audience: 'tenant', customizable: true },
  'access.view': { label: 'View access requests', audience: 'both', customizable: true },
  'access.declare': { label: 'Declare access granted', audience: 'both', customizable: true },
  'access.request': { label: 'Create access requests', audience: 'tenant', customizable: true },
  'access.verify': { label: 'Verify access', audience: 'tenant', customizable: true },
  // Work
  'task.view': { label: 'View tasks', audience: 'both', customizable: true },
  'task.manage': { label: 'Manage tasks', audience: 'tenant', customizable: true },
  'task.interact': { label: 'Update assigned tasks', audience: 'both', customizable: true },
  'comment.create': { label: 'Comment', audience: 'both', customizable: true },
  'note.internal': { label: 'Read & write internal notes', audience: 'tenant', customizable: true },
  'waiver.grant': { label: 'Grant waivers', audience: 'tenant', customizable: true },
  'submission.view': { label: 'View submitted revisions', audience: 'both', customizable: true },
  'approval.create': { label: 'Approve submissions', audience: 'both', customizable: true },
  'changes.request': { label: 'Request changes', audience: 'both', customizable: true },
  // Reporting / audit
  'reports.view': { label: 'View reports', audience: 'tenant', customizable: true },
  'audit.view': { label: 'View audit log', audience: 'tenant', customizable: true },
  'export.create': { label: 'Create exports', audience: 'both', customizable: true },
  'support.grant': { label: 'Grant platform support access', audience: 'tenant', customizable: false },
  // Platform
  'platform.tenants.manage': { label: 'Manage tenants', audience: 'platform', customizable: false },
  'platform.plans.manage': { label: 'Manage plans', audience: 'platform', customizable: false },
} as const;

export type Capability = keyof typeof CAPABILITIES;

type Grant = Partial<Record<Capability, Scope>>;

const all = (scope: Scope, caps: Capability[]): Grant =>
  Object.fromEntries(caps.map((c) => [c, scope])) as Grant;

const TENANT_ADMIN_CAPS: Capability[] = [
  'staff.view', 'staff.manage', 'client.view', 'client.create', 'client.edit', 'client.archive',
  'client.profile.update', 'templates.view', 'templates.manage', 'packages.manage', 'workflow.manage',
  'automation.manage', 'engagement.view', 'engagement.create', 'engagement.manage', 'engagement.migrate',
  'engagement.handoff', 'invitation.manage', 'participant.manage', 'form.view', 'form.answer.edit',
  'form.review', 'asset.view', 'asset.upload', 'asset.review', 'access.view', 'access.request',
  'access.verify', 'task.view', 'task.manage', 'task.interact', 'comment.create', 'note.internal',
  'waiver.grant', 'submission.view', 'changes.request', 'reports.view', 'audit.view', 'export.create',
  'tenant.settings.manage',
];

export const DEFAULT_ROLE_GRANTS: Record<Role, Grant> = {
  platform_operator: all('tenant', ['platform.tenants.manage', 'platform.plans.manage']),
  tenant_owner: {
    ...all('tenant', TENANT_ADMIN_CAPS),
    ...all('tenant', ['tenant.billing.manage', 'tenant.ownership.transfer', 'roles.manage', 'support.grant']),
  },
  tenant_admin: all('tenant', TENANT_ADMIN_CAPS),
  account_manager: {
    ...all('tenant', ['client.create', 'templates.view', 'staff.view', 'engagement.create', 'reports.view']),
    // Clients are assignment-scoped: a manager sees and edits only clients they manage (or work on).
    ...all('assigned', [
      'client.view', 'client.edit', 'client.profile.update', 'engagement.view', 'engagement.manage', 'engagement.handoff', 'invitation.manage',
      'participant.manage', 'form.view', 'form.answer.edit', 'form.review', 'asset.view', 'asset.upload',
      'asset.review', 'access.view', 'access.request', 'access.verify', 'task.view', 'task.manage',
      'task.interact', 'comment.create', 'note.internal', 'waiver.grant', 'submission.view',
      'changes.request', 'export.create',
    ]),
  },
  specialist: {
    ...all('tenant', ['templates.view']),
    ...all('assigned', [
      'client.view', 'engagement.view', 'form.view', 'form.review', 'asset.view', 'asset.review',
      'access.view', 'access.verify', 'task.view', 'task.interact', 'comment.create', 'note.internal',
      'submission.view', 'changes.request',
    ]),
  },
  auditor: all('tenant', [
    'staff.view', 'client.view', 'templates.view', 'engagement.view', 'form.view', 'asset.view',
    'access.view', 'task.view', 'submission.view', 'reports.view', 'audit.view', 'note.internal',
    // Exports are read-only snapshots; auditors need them for audit evidence.
    'export.create',
  ]),
  client_admin: all('client', [
    'client.view',
    'engagement.view', 'invitation.manage', 'participant.manage', 'form.view', 'form.answer.edit',
    'form.submit', 'asset.view', 'asset.upload', 'access.view', 'access.declare', 'task.view',
    'task.interact', 'comment.create', 'submission.view', 'client.profile.update', 'export.create',
  ]),
  client_contributor: all('client', [
    'client.view',
    'engagement.view', 'form.view', 'form.answer.edit', 'form.submit', 'asset.view', 'asset.upload',
    'access.view', 'access.declare', 'task.view', 'task.interact', 'comment.create', 'submission.view',
  ]),
  client_approver: all('client', [
    'client.view',
    'engagement.view', 'form.view', 'asset.view', 'access.view', 'task.view', 'comment.create',
    'submission.view', 'approval.create', 'changes.request',
  ]),
};

/** Roles a client administrator may assign inside its own client boundary. */
export const CLIENT_ASSIGNABLE_BY_CLIENT_ADMIN: ClientRole[] = ['client_contributor', 'client_approver'];

export interface RoleCapabilityOverride {
  role: Role;
  add?: Partial<Record<Capability, Scope>>;
  remove?: Capability[];
}

const SCOPE_RANK: Record<Scope, number> = { client: 0, assigned: 1, tenant: 2 };

/**
 * Resolve effective grants for a role, applying tenant overrides only for customizable
 * capabilities whose audience matches the role kind. Client roles can never gain
 * tenant-audience capabilities and never gain wider than `client` scope.
 */
export function resolveRoleGrants(role: Role, overrides: RoleCapabilityOverride[] = []): Grant {
  const grants: Grant = { ...DEFAULT_ROLE_GRANTS[role] };
  if (role === 'platform_operator' || role === 'tenant_owner') return grants;
  const clientRole = isClientRole(role);
  for (const o of overrides.filter((x) => x.role === role)) {
    for (const cap of o.remove ?? []) {
      if (CAPABILITIES[cap]?.customizable) delete grants[cap];
    }
    for (const [cap, scope] of Object.entries(o.add ?? {}) as [Capability, Scope][]) {
      const def = CAPABILITIES[cap] as { customizable: boolean; audience: string } | undefined;
      if (!def || !def.customizable) continue;
      if (clientRole) {
        if (def.audience === 'tenant' || def.audience === 'platform') continue;
        // Overrides may narrow a client role, never turn a contributor into an approver/admin.
        if (!DEFAULT_ROLE_GRANTS[role][cap]) continue;
        grants[cap] = 'client';
      } else {
        if (def.audience === 'platform') continue;
        if (scope === 'client') continue;
        grants[cap] = scope;
      }
    }
  }
  return grants;
}

export function widestScope(a: Scope | undefined, b: Scope | undefined): Scope | undefined {
  if (!a) return b;
  if (!b) return a;
  return SCOPE_RANK[a] >= SCOPE_RANK[b] ? a : b;
}
